import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { AuthSplit, Field } from "./SignIn";
import { Loader2, ArrowRight, AlertCircle } from "lucide-react";
import PersonaPicker from "../components/PersonaPicker";

/**
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
function googleSignIn() {
  const redirectUrl = window.location.origin + "/";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function SignUp() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [persona, setPersona] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await register(email, password, name, persona);
      navigate("/", { replace: true });
    } catch (err) {
      setError(err.message || "Sign up failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthSplit>
      <div className="max-w-md w-full">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#5C5C59] mb-3">
          Create account
        </div>
        <h1 className="font-heading font-bold tracking-tight text-4xl text-brand leading-none">
          Start your free trial.
        </h1>
        <p className="text-[#5C5C59] mt-3">
          For councils, combined authorities, operators and investors.
        </p>

        <button
          type="button"
          onClick={googleSignIn}
          data-testid="google-signup-btn"
          className="w-full mt-8 flex items-center justify-center gap-3 border border-brand-border bg-white text-[#111110] rounded-md px-4 py-3 text-sm font-medium hover:bg-brand-bg transition-colors"
        >
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-xs text-[#5C5C59] font-mono uppercase tracking-wider">
          <div className="flex-1 h-px bg-brand-border" />
          or
          <div className="flex-1 h-px bg-brand-border" />
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <Field label="Full name" type="text" autoComplete="name" value={name} onChange={setName} testid="name-input" />
          <Field label="Work email" type="email" autoComplete="email" value={email} onChange={setEmail} testid="email-input" required />
          <Field label="Password" type="password" autoComplete="new-password" value={password} onChange={setPassword} testid="password-input" required />
          <p className="text-[10px] font-mono uppercase tracking-wider text-[#5C5C59]">Minimum 8 characters.</p>
          <PersonaPicker value={persona} onChange={setPersona} />
          {error && (
            <div className="flex items-start gap-2 text-sm text-[#991B1B] bg-[#FEF2F2] border border-[#FECACA] rounded-md p-3" data-testid="signup-error">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          <button
            type="submit"
            disabled={busy}
            data-testid="signup-submit"
            className="w-full bg-brand text-white rounded-md px-4 py-3 text-sm font-medium hover:bg-brand-deep transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Create account <ArrowRight className="h-4 w-4" /></>}
          </button>
        </form>

        <div className="mt-6 text-sm text-[#5C5C59]">
          Already have an account?{" "}
          <Link to="/signin" className="text-brand hover:text-brand-deep font-medium" data-testid="goto-signin">
            Sign in
          </Link>
        </div>
      </div>
    </AuthSplit>
  );
}
